import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const financings = {
    layout: {
        listTitle: 'Financings',
        listTitleLoan: 'Loans',
        detailTitle: 'Financing',
        detailTitleLoan: 'Loan',
        formTitle: 'New Financing',
    },
    list: {
        summary: {
            activeLabel: 'Active',
            monthlyCommitmentLabel: 'Monthly commitment',
        },
        status: {
            settled: 'Paid off',
            archived: 'Archived',
        },
        noInstitution: 'No institution',
        nextInstallment: 'Installment #{{number}}',
        nextInstallmentWithDue: 'Installment #{{number}} · due {{date}}',
        noPendingInstallments: 'No pending installments',
        amountPerMonth: '{{amount}}/mo',
        remainingCount_one: '{{count}} remaining',
        remainingCount_other: '{{count}} remaining',
        installmentCount_one: '{{count}} installment',
        installmentCount_other: '{{count}} installments',
        emptyTitle: 'No active financings',
        emptySubtitle: 'Add a contract to track installments and paydowns in the normal app flow.',
        emptyAction: 'New financing',
        emptyTitleLoan: 'No active loans',
        emptySubtitleLoan: 'A bank or fintech loan: track the installments, pay in parts and add late interest.',
        emptyActionLoan: 'New loan',
        endedSection: 'Ended',
    },
    detail: {
        monthlyInstallmentLabel: 'Monthly installment',
        remainingLabel: 'Remaining',
        progressPaid: '{{paid}} of {{total}} paid',
        balanceHeroLabel: 'Outstanding balance today',
        balanceHeroInformedLabel: 'Outstanding balance reported',
        balanceHeroFallbackLabel: 'Left to pay in installments',
        balanceUpdatedShort: 'on {{date}}',
        balanceHeroSplitHint: 'of the next {{installment}} installment, {{amortization}} cuts the debt — the rest is this month\u2019s interest',
        balanceInformedLine: 'Reported balance: {{value}} on {{date}}',
        balanceInformedLineNoDate: 'Reported balance: {{value}}',
        balanceHeroMissingRateHint: 'Without the contract rate the app cannot work out the outstanding balance. Enter the rate under Edit contract → Calculation → Price or SAC so the balance drops with every installment.',
        payingWith: 'debits {{account}}',
        rulerPaid_one: '{{count}} paid',
        rulerPaid_other: '{{count}} paid',
        rulerRemaining_one: '{{count}} left',
        rulerRemaining_other: '{{count}} left',
        rulerRemainingUntil: '{{remaining}} · through {{date}}',
        nextInstallmentOf: 'Installment {{number}} of {{total}}',
        dueToday: 'due today',
        dueInDays_one: 'due in {{count}} day',
        dueInDays_other: 'due in {{count}} days',
        overdueDays_one: '{{count}} day overdue',
        overdueDays_other: '{{count}} days overdue',
        payButton: 'Pay',
        paidShortLabel: 'Paid so far',
        interestShortLabel: 'Projected interest',
        amortizationShortLabel: 'Cuts the debt',
        prepaymentShortLabel: 'Pay down',
        groupUpcoming: 'Upcoming',
        groupPaid: 'Paid',
        paidSoFarLabel: 'Paid so far',
        pendingAmountLabel: 'Remaining in installments',
        nextInstallment: 'Next installment: {{number}}',
        nextInstallmentWithDue: 'Next installment: {{number}} · due {{date}}',
        outstandingBalance: 'Outstanding balance: {{balance}}',
        outstandingBalanceWithDate: 'Outstanding balance: {{balance}} · updated {{date}}',
        estimatedInterest: 'Estimated interest over the schedule: {{amount}}',
        recordPrepaymentButton: 'Record paydown',
        allInstallmentsPaidHint: 'All installments are paid — there is nothing to pay down.',
        transactionsSection: 'Transactions',
        pendingCount_one: '{{count}} pending',
        pendingCount_other: '{{count}} pending',
        confirmedCount_one: '{{count}} completed',
        confirmedCount_other: '{{count}} completed',
        numberingNote: 'Numbering follows the original contract ({{total}}x); paydowns remove the last installments.',
        numberingNoteLoan: 'Numbering follows the original contract ({{total}}x); paydowns remove installments from the series.',
        emptyTitle: 'No transactions for this contract',
        emptySubtitle: 'Regular installments and paydowns show up here.',
        adjustments: {
            title: 'Paydown history',
            empty: 'No paydowns recorded yet.',
            remainingInstallments_one: '{{count}} installment remaining',
            remainingInstallments_other: '{{count}} installments remaining',
            balance: 'Balance: {{balance}}',
            deleteA11y: 'Delete paydown from {{date}}',
        },
    },
    partialPayment: {
        alreadyPaidNote: '{{amount}} already paid on this installment',
        deleteTitle: 'Delete partial payment',
        deleteMessage: '{{amount}} goes back to the open installment.',
        deleteConfirm: 'Delete',
    },
    charges: {
        amountLabel: 'Interest and late fee',
        helper: 'Total charges on this installment. Set to zero to restore the contract amount.',
        baseLabel: 'Installment amount',
        alreadyPaidLabel: 'Already paid on this installment',
        newTotalLabel: 'New installment total',
        confirmButton: 'Save charges',
        includedNote: 'includes {{amount}} in charges',
    },
    prepayment: {
        confirmButton: 'Confirm paydown',
        amountLabel: 'Amount paid',
        subtitle: '{{name}} · extra payment to pay down the outstanding balance',
        datePlaceholder: 'Date',
        dateHelper: 'Date of the extra payment',
        modeTerm: 'Reduce term',
        modeInstallment: 'Reduce installment',
        modeAdvance: 'Pay ahead',
        modeTermHelper: 'Removes the last installments; the monthly amount stays the same.',
        modeInstallmentHelper: 'Lowers the amount of the pending installments; the term stays the same.',
        modeAdvanceHelper: 'Covers the next installment and moves on to the following ones if there is money left; the rest keep the agreed amount.',
        advanceLabel: 'What this payment covers',
        advanceHintEmpty: 'Enter the amount paid to see which installments it covers.',
        advanceHintSettled_one: 'Settles the next installment.',
        advanceHintSettled_other: 'Settles the next {{count}} installments.',
        advanceHintPartial: 'The next installment drops to {{amount}}.',
        advanceHintSettledAndPartial_one: 'Settles the next installment; the following one drops to {{amount}}.',
        advanceHintSettledAndPartial_other: 'Settles the next {{count}} installments; the following one drops to {{amount}}.',
        advanceHintExceeds: 'More than the pending total ({{total}}) — to close the contract, pay the installments instead.',
        advanceNoteSettled_one: '1 installment settled by the advance payment',
        advanceNoteSettled_other: '{{count}} installments settled by the advance payment',
        advanceNotePartial: 'installment #{{number}} lowered to {{amount}}',
        remainingLabel: 'Remaining installments',
        remainingHintEmpty: 'Enter how many installments remain after the paydown (today: {{current}})',
        installmentsRemoved_one: 'The last installment will be removed',
        installmentsRemoved_other: 'The last {{count}} installments will be removed',
        noneRemoved: 'No installments will be removed',
        moreThanCurrent: 'More than the current {{total}} installments',
        useSuggestionRemaining: 'Use suggestion: {{value}} (amount paid ÷ installment)',
        newMonthlyAmountLabel: 'New installment amount',
        newMonthlyAmountHelper: 'Applies to the {{total}} pending installments (today: {{amount}}).',
        useSuggestionAmount: 'Use suggestion: {{value}} (remaining balance ÷ installments)',
        newBalanceLabel: 'New outstanding balance',
        balanceHelperKnown: 'Calculated: current balance ({{balance}}) − amount paid. Adjust if your bank reports a different value.',
        balanceHelperEstimated: 'Calculated: pending installments ({{balance}}) − amount paid, without deducting interest. Adjust to the value from your bank.',
        balanceHelperEmpty: 'Balance left to pay after the paydown, per your bank.',
        withoutCashLabel: "Don't debit an account",
        withoutCashHelper: 'Paid with FGTS or another resource outside the app — it only enters the financing history, without debiting any account.',
        notesPlaceholder: 'Note (optional)',
    },
    edit: {
        recalibrateTitle: 'Adjust projection to the bill',
        recalibrateMessage: 'Instalment {{number}} was projected at {{expected}} and the bill came at {{observed}}. Apply the {{diff}} difference to future instalments as insurance and fees?',
        recalibrateConfirm: 'Adjust projection',
        recalibrateAction: 'Adjust projection to the real amount',
    },
    menu: {
        editContract: 'Edit contract',
        archiveSettle: 'Archive/Pay off',
        delete: 'Delete contract',
    },
    deleteAdjustmentDialog: {
        title: 'Delete paydown',
        message: 'Removes the {{amount}} extra payment and returns to the schedule the installments it had removed. The outstanding balance goes back to that of the previous paydown (or is cleared) and the account balance is recalculated.',
        confirm: 'Delete paydown',
    },
    deleteDialog: {
        title: 'Delete contract',
        message: 'Deletes the contract, ALL installments (including paid ones) and paydowns. Account balances are recalculated. This action cannot be undone. If you just want to stop tracking while keeping the history, use Archive/Pay off.',
        confirm: 'Delete everything',
    },
    archiveDialog: {
        title: 'Archive contract',
        message: 'Future pending installments will be removed from the normal app flow. Continue?',
        confirm: 'Archive',
    },
    pdf: {
        title: 'Financing — {{name}}',
        titleLoan: 'Loan — {{name}}',
        monthlyAmountLabel: 'Monthly installment',
        progressLabel: 'Installments paid',
        progressValue: '{{paid}} of {{total}}',
        paidAmountLabel: 'Paid so far',
        pendingAmountLabel: 'Left in installments',
        outstandingBalanceLabel: 'Outstanding balance',
        outstandingBalanceInformedLabel: 'Outstanding balance reported',
        balanceUpdatedAt: 'updated on {{date}}',
        installmentsSection: 'Installments',
        adjustmentsSection: 'Paydowns',
        partialPaymentsSection: 'Partial payments',
        colNumber: 'No.',
        colDate: 'Date',
        colAmount: 'Amount',
        colStatus: 'Status',
        colInstallment: 'Installment',
        colRemaining: 'Remaining',
        colBalanceAfter: 'New balance',
        statusPaid: 'Paid',
        statusPending: 'Pending',
        noInstallments: 'No installments recorded.',
        noAdjustments: 'No paydowns recorded.',
        noPartialPayments: 'No partial payments recorded.',
        exportError: 'Could not generate the PDF.',
    },
} satisfies DeepPartialStrings<TranslationSchema['financings']>;
